<script setup>
import { computed } from 'vue';

import CloseIcon from '@/components/icons/IconClose.vue';
import InfoIcon from '@/components/icons/IconInfo.vue';
import { useToastStore } from '@/stores/toast.js';

const props = defineProps({
  topPosition: {
    type: String,
    default: '0.45rem',
  },
  bottomPosition: {
    type: String,
    default: '0.45rem',
  },
});

const toastStore = useToastStore();
const messages = computed(() => toastStore.messages);
</script>

<template>
  <transition-group name="fade" tag="div" class="toast-container" :style="{ top: topPosition }">
    <div v-for="toast in messages" :key="toast.id" class="toast-message" :class="toast.type">
      <InfoIcon v-if="toast.type === 'info'" class="info-icon" />
      <InfoIcon v-else-if="toast.type === 'warn'" class="info-icon" />
      <InfoIcon v-else class="info-icon" />

      <p class="medium toast-content">{{ toast.message }}</p>

      <CloseIcon class="close-icon" @click="() => toastStore.closeToast(toast.id)" />
    </div>
  </transition-group>
</template>

<style scoped lang="scss">
.toast-container {
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 1rem;
  z-index: 9999;
}

.toast-message {
  display: flex;
  width: fit-content;
  min-width: 5rem;
  max-width: 20rem;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-top: 0.125rem;
  align-self: flex-start;
  border-radius: 8px;
  background-color: var(--surface-primary);
  box-shadow: $shadow-toast;
}

.toast-message.info {
  background-color: var(--surface-primary);
}

.toast-message.warn {
  background-color: var(--warning);
}

.toast-message.error {
  background-color: var(--error);
}

.info-icon {
  position: relative;
  width: 1rem;
  height: 1rem;
  top: 0.25rem;
  fill: var(--font-white);
  flex-shrink: 0;
}

.warn-icon {
  position: relative;
  width: 1rem;
  height: 1rem;
  top: 0.25rem;
  fill: var(--font-white);
  flex-shrink: 0;
}

.error-icon {
  position: relative;
  width: 1rem;
  height: 1rem;
  top: 0.25rem;
  fill: var(--font-white);
  flex-shrink: 0;
}

.toast-content {
  width: 100%;
  color: var(--font-white);
}

.close-icon {
  position: relative;
  top: 0.125rem;
  width: 1.25rem;
  height: 1.25rem;
  fill: var(--font-white);
  color: var(--font-white);
  opacity: 0.5;
  flex-shrink: 0;
  cursor: pointer;
}

.close-icon:hover {
  opacity: 0.2;
  cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    transform 0.3s ease-out,
    opacity 0.3s ease-out;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(-2rem);
}

.fade-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-2rem);
}
</style>
